import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Fade,
  Grow,
  Collapse,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  ShoppingCart as CartIcon,
  Email as EmailIcon,
  Storefront as StoreIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';
import { API_BASE_URL } from '../config';

interface CartItem {
  _id: string;
  assetId: string;
  assetName: string;
  assetFileName: string;
  quantity: number;
  stockLevel: number;
  addedAt: string;
  previewPath?: string;
  isAvailable: boolean;
}

interface CheckoutData {
  customerName: string;
  customerEmail: string;
  customerMessage: string;
}

interface CartModalProps {
  open: boolean;
  onClose: () => void;
  onCartUpdate?: () => void;
}

// Component for individual quantity input to maintain local state and prevent focus loss
const QuantityInput: React.FC<{
  item: CartItem;
  onUpdate: (itemId: string, quantity: number) => void;
}> = ({ item, onUpdate }) => {
  const [localQuantity, setLocalQuantity] = useState(item.quantity);

  // Update local state when item quantity changes from outside
  useEffect(() => {
    setLocalQuantity(item.quantity);
  }, [item.quantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(e.target.value) || 1;
    setLocalQuantity(newQuantity);
  };

  const handleBlur = () => {
    // Validate and update when user leaves the field
    const validQuantity = Math.max(1, Math.min(localQuantity, item.stockLevel));
    if (validQuantity !== item.quantity) {
      onUpdate(item._id, validQuantity);
    }
    setLocalQuantity(validQuantity);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Update when user presses Enter
    if (e.key === 'Enter') {
      handleBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <TextField
      type="number"
      value={localQuantity}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      size="small"
      inputProps={{ 
        min: 1, 
        max: item.stockLevel,
        style: { textAlign: 'center', width: '60px' }
      }}
      sx={{ 
        width: '80px',
        '& .MuiOutlinedInput-root': {
          height: '32px'
        }
      }}
    />
  );
};

const CartModal: React.FC<CartModalProps> = ({ open, onClose, onCartUpdate }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    customerName: '',
    customerEmail: '',
    customerMessage: ''
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Fetch cart items
  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/cart`);
      setCartItems(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setError('Kunde inte ladda varukorg');
    } finally {
      setLoading(false);
    }
  };

  // Update cart item quantity
  const updateCartItem = async (itemId: string, quantity: number) => {
    try {
      await axiosInstance.put(`/api/cart/${itemId}`, { quantity });
      fetchCart();
      if (onCartUpdate) onCartUpdate();
    } catch (error: any) {
      console.error('Error updating cart:', error);
      setError(error.response?.data?.error || 'Kunde inte uppdatera varukorg');
    }
  };

  // Remove item from cart
  const removeFromCart = async (itemId: string) => {
    try {
      await axiosInstance.delete(`/api/cart/${itemId}`);
      fetchCart();
      if (onCartUpdate) onCartUpdate();
    } catch (error: any) {
      console.error('Error removing from cart:', error);
      setError(error.response?.data?.error || 'Kunde inte ta bort från varukorg');
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (cartItems.length === 0) return;
    try {
      // Remove all items one by one
      await Promise.all(cartItems.map(item => axiosInstance.delete(`/api/cart/${item._id}`)));
      fetchCart();
      if (onCartUpdate) onCartUpdate();
      setSuccess('Varukorgen har tömts');
    } catch (error: any) {
      console.error('Error clearing cart:', error);
      setError(error.response?.data?.error || 'Kunde inte tömma varukorg');
    }
  };

  // Submit order
  const submitOrder = async () => {
    if (!checkoutData.customerName || !checkoutData.customerEmail) {
      setError('Namn och e-post är obligatoriska');
      return;
    }

    try {
      setSubmittingOrder(true);
      await axiosInstance.post(`/api/cart/checkout`, checkoutData);
      setSuccess('Beställning skickad! Du kommer att få en bekräftelse via e-post.');
      setCheckoutOpen(false);
      setCheckoutData({ customerName: '', customerEmail: '', customerMessage: '' });
      fetchCart(); // Refresh cart (should be empty now)
      if (onCartUpdate) onCartUpdate();
    } catch (error: any) {
      console.error('Error submitting order:', error);
      setError(error.response?.data?.error || 'Kunde inte skicka beställning');
    } finally {
      setSubmittingOrder(false);
    }
  };

  // Open default email client with order details
  const openEmailClient = () => {
    const orderDetails = cartItems.map(item => 
      `${item.assetName} - Antal: ${item.quantity}`
    ).join('\n');

    const subject = encodeURIComponent(`Beställning från ${checkoutData.customerName || 'Kund'}`);
    const body = encodeURIComponent(`Hej!

Jag skulle vilja beställa följande artiklar:

${orderDetails}

Kontaktuppgifter:
Namn: ${checkoutData.customerName}
E-post: ${checkoutData.customerEmail}

${checkoutData.customerMessage ? `Meddelande:\n${checkoutData.customerMessage}` : ''}

Tack!
`);

    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
  };

  // Get total items count
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Load cart when modal opens
  useEffect(() => {
    if (open) {
      fetchCart();
    }
  }, [open]);

  // Clear messages after some time
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <>
      {/* Main Cart Dialog */}
      <Dialog
        open={open}
        onClose={() => { setCheckoutOpen(false); onClose(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon />
            {checkoutOpen ? 'Skicka beställning' : `Varukorg (${getTotalItems()} artikel${getTotalItems() !== 1 ? 'ar' : ''})`}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Stepper - visible from step 1 when cart has items */}
          {!loading && cartItems.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stepper activeStep={checkoutOpen ? 1 : 0} alternativeLabel>
                <Step completed={checkoutOpen}>
                  <StepLabel>Varukorg</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Kontaktuppgifter</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Bekräftelse</StepLabel>
                </Step>
              </Stepper>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : cartItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Varukorgen är tom
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lägg till artiklar från butiksmaterial-kategorin
              </Typography>
            </Box>
          ) : (
            <List>
              {cartItems.map((item, index) => (
                <Grow 
                  key={item._id} 
                  in={true} 
                  timeout={{ enter: 300 + index * 50 }}
                  style={{ transformOrigin: '0 0 0' }}
                >
                  <Box>
                    <ListItem 
                      sx={{ 
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          transform: 'scale(1.01)'
                        }
                      }}
                    >
                      <ListItemAvatar sx={{ mr: 2 }}>
                        {item.previewPath ? (
                          <Avatar
                            src={`${API_BASE_URL}${item.previewPath}`}
                            alt={item.assetName}
                            variant="rounded"
                            sx={{ width: 80, height: 80, borderRadius: '8px' }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 80, height: 80, borderRadius: '8px', bgcolor: 'grey.200' }}>
                            <StoreIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight={500}>
                            {item.assetName}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>
                              {item.assetFileName}
                            </Typography>
                            <Chip
                              label={`${item.stockLevel} i lager`}
                              size="small"
                              sx={{ 
                                mt: 0.5, 
                                width: 'fit-content',
                                bgcolor: item.stockLevel > 50 ? 'success.light' : item.stockLevel >= 10 ? 'warning.light' : 'error.light',
                                color: item.stockLevel > 50 ? 'success.dark' : item.stockLevel >= 10 ? 'warning.dark' : 'error.dark',
                                fontWeight: 500
                              }}
                            />
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            onClick={() => updateCartItem(item._id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            size="small"
                            sx={{ 
                              transition: 'transform 0.15s',
                              '&:hover': { transform: 'scale(1.1)' }
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          
                          <QuantityInput item={item} onUpdate={updateCartItem} />
                          
                          <IconButton
                            onClick={() => updateCartItem(item._id, item.quantity + 1)}
                            disabled={item.quantity >= item.stockLevel}
                            size="small"
                            sx={{ 
                              transition: 'transform 0.15s',
                              '&:hover': { transform: 'scale(1.1)' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                          
                          <IconButton
                            onClick={() => removeFromCart(item._id)}
                            color="error"
                            size="small"
                            sx={{ 
                              transition: 'transform 0.15s',
                              '&:hover': { transform: 'scale(1.1)' }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    {index < cartItems.length - 1 && <Divider />}
                  </Box>
                </Grow>
              ))}
            </List>
          )}
          
          {/* Summary footer - only show when not in checkout mode */}
          <Collapse in={cartItems.length > 0 && !checkoutOpen}>
            <Box sx={{ 
              mt: 2, 
              pt: 2, 
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="body1" fontWeight={500}>
                Totalt:
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {getTotalItems()} artikel{getTotalItems() !== 1 ? 'ar' : ''} ({cartItems.length} produkt{cartItems.length !== 1 ? 'er' : ''})
              </Typography>
            </Box>
          </Collapse>

          {/* Inline Checkout Form */}
          <Collapse in={checkoutOpen} timeout={400}>
            <Fade in={checkoutOpen} timeout={500}>
              <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid', borderColor: 'primary.main' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fyll i dina kontaktuppgifter för att skicka beställningen
                </Typography>
                
                <TextField
                  label="Namn *"
                  fullWidth
                  value={checkoutData.customerName}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, customerName: e.target.value }))}
                  sx={{ mb: 2 }}
                  disabled={submittingOrder}
                  size="small"
                />
                
                <TextField
                  label="E-post *"
                  type="email"
                  fullWidth
                  value={checkoutData.customerEmail}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  sx={{ mb: 2 }}
                  disabled={submittingOrder}
                  size="small"
                />
                
                <TextField
                  label="Meddelande (valfritt)"
                  multiline
                  rows={3}
                  fullWidth
                  value={checkoutData.customerMessage}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, customerMessage: e.target.value }))}
                  placeholder="Lägg till eventuella kommentarer eller instruktioner..."
                  disabled={submittingOrder}
                  size="small"
                />

                {/* Order summary in checkout */}
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Beställningsöversikt:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getTotalItems()} artikel{getTotalItems() !== 1 ? 'ar' : ''} från {cartItems.length} produkt{cartItems.length !== 1 ? 'er' : ''}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Collapse>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          {/* Left side buttons */}
          <Box>
            {cartItems.length > 0 && !checkoutOpen && (
              <Button
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={clearCart}
                size="small"
              >
                Töm varukorg
              </Button>
            )}
            {checkoutOpen && (
              <Button
                onClick={() => setCheckoutOpen(false)}
                disabled={submittingOrder}
              >
                ← Tillbaka
              </Button>
            )}
          </Box>

          {/* Right side buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!checkoutOpen ? (
              <>
                <Button onClick={onClose}>
                  Stäng
                </Button>
                {cartItems.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<CartIcon />}
                    onClick={() => setCheckoutOpen(true)}
                  >
                    Beställ material
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  startIcon={<LaunchIcon />}
                  onClick={() => {
                    if (checkoutData.customerName && checkoutData.customerEmail) {
                      openEmailClient();
                      setCheckoutOpen(false);
                    } else {
                      setError('Namn och e-post krävs för att öppna mailprogrammet');
                    }
                  }}
                  disabled={!checkoutData.customerName || !checkoutData.customerEmail}
                  variant="outlined"
                >
                  Via mailprogram
                </Button>
                <Button
                  variant="contained"
                  onClick={submitOrder}
                  disabled={submittingOrder || !checkoutData.customerName || !checkoutData.customerEmail}
                  startIcon={submittingOrder ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                >
                  {submittingOrder ? 'Skickar...' : 'Skicka beställning'}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CartModal; 